import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { Button, Radio, Checkbox, Upload } from 'antd';
import DrawerModal from '../../components/shared/DrawerModal';
import CarStatusSelector from '../../components/CarEditDrawer/CarStatusSelector';
import BlockedReasonSelector from '../../components/CarEditDrawer/BlockedReasonSelector';
import CAR_STATUSES from '../../libs/carStatuses';
import styles from './UpdateCarStatusModal.styles';
import CircleSelector from './CircleSelector';
import {
  updateCarIfNeeded,
  selectSelectedCar,
  createStatusChangeBooking,
  getActiveBooking,
  selectTimeTableCars,
  uploadCarDocument,
  selectCarActiveBookings,
} from '../../redux/store/userData/cars';
import { fetchVehicleEventsForCar } from '../../redux/store/userData/vehicleEvents';
import {
  getIsVisibleModal,
  closeModal,
  openModal,
} from '../../redux/store/ui/common';
import { selectToken } from '../../redux/store/userData/user';
import { useIntl } from 'react-intl';
import { DatePicker } from 'antd/es';
import { isBefore } from 'date-fns';
import { RadioChangeEvent } from 'antd/lib/radio/interface';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { updateBooking } from '../../redux/store/userData/trips';
import ConfirmBlockModal from './ConfirmBlockModal';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import isDesktop from '../../utils/sizeHelpers';

const THE_FAR_FUTURE = '2050-05-05T20:00:00Z';

type State = {
  nextStatus: string | undefined;
  blockedReasons?: string[];
  comment: string;
  circle: string;
};

const getRequestBodyFromState = (state: State) => {
  let body;

  if (state.nextStatus === CAR_STATUSES.RELEASE_READY) {
    body = state.comment
      ? {
          status: state.nextStatus,
          noteText: state.comment,
        }
      : {
          status: state.nextStatus,
        };
  }

  if (state.nextStatus === CAR_STATUSES.RELEASED) {
    body = state.comment
      ? {
          status: state.nextStatus,
          availableUntil: THE_FAR_FUTURE,
          noteText: state.comment,
          circles: state.circle ? [state.circle] : [],
        }
      : {
          status: state.nextStatus,
          availableUntil: THE_FAR_FUTURE,
          circles: state.circle ? [state.circle] : [],
        };
  }

  if (state.nextStatus === CAR_STATUSES.BLOCKED) {
    body = state.comment
      ? {
          status: state.nextStatus,
          blockReasons: state.blockedReasons,
          noteText: state.comment,
        }
      : {
          status: state.nextStatus,
          blockReasons: state.blockedReasons,
        };
  }

  return body;
};

const getBase64 = (file): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

const UpdateCarStatusModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const selectedCar = useSelector(selectSelectedCar);
  const displayCars = useSelector(selectTimeTableCars);
  const carBookings = useSelector(selectCarActiveBookings);

  const [state, setState] = useState<State>({
    nextStatus: selectedCar?.status,
    blockedReasons: selectedCar?.blockReasons,
    comment: '',
    circle: '',
  });
  const [isClearComment, setClearComment] = useState(false);
  const [statusChaneType, setStatusChangeType] = useState(
    selectedCar?.status === CAR_STATUSES.RENTED ? 2 : 1
  );
  const [isWithoutEndDate, setIsWithoutEndDate] = useState(false);
  const [startDate, setStartDate] = useState(null) as any;
  const [endDate, setEndDate] = useState(null) as any;
  const [isAddPhotoEnabled, setIsAddPhotoEnabled] = useState(false);
  const [statusImages, setStatusImages] = useState([]);
  const [imageURI, setImageURI] = useState('');

  const visibleModal = useSelector(getIsVisibleModal('updateStateModal'));
  const currentStatus = selectedCar?.status;

  useEffect(() => {
    setState({
      ...state,
      nextStatus: currentStatus,
      blockedReasons: selectedCar?.blockReasons,
    });
  }, [selectedCar, setState]);

  useEffect(() => {
    setState({
      ...state,
      comment: '',
      circle: '',
    });
    setClearComment(false);
    setStatusChangeType(selectedCar?.status === CAR_STATUSES.RENTED ? 2 : 1);
  }, [visibleModal]);

  const onSelectCarStatus = (value: any) => {
    setState({ ...state, nextStatus: value });
    setStatusImages([]);
    setImageURI('');
  };

  const onSelectReason = (value: any) => {
    setState({ ...state, blockedReasons: value });
  };

  const onSelectCircle = (value: any) => {
    setState({ ...state, circle: value });
  };

  const clearStatusImages = () => {
    setIsAddPhotoEnabled(false);
  };

  const isStatusChange = state.nextStatus !== currentStatus;
  const isNoteOnlyUpdate =
    !isStatusChange && state.nextStatus !== CAR_STATUSES.BLOCKED;
  const hasNote = isClearComment || !!state.comment;
  const hasImage = isAddPhotoEnabled && !!imageURI;

  const submitPhotos = () => {
    if (hasImage) {
      dispatch(
        uploadCarDocument(selectedCar?.id, imageURI, 'OTHER', () => {
          dispatch(fetchVehicleEventsForCar(selectedCar?.id!));
        })
      );

      clearStatusImages();
    }
  };

  const handleOnSubmit = (
    e: { preventDefault: () => void },
    proceed: boolean = false
  ) => {
    e.preventDefault();
    const body = isNoteOnlyUpdate
      ? hasNote && { noteText: state.comment }
      : getRequestBodyFromState(state);

    if (
      state.nextStatus === CAR_STATUSES.BLOCKED &&
      carBookings.length > 0 &&
      !proceed
    ) {
      dispatch(openModal('ConfirmBlockModal'));
      return;
    }

    if (statusChaneType === 1 || isNoteOnlyUpdate) {
      batch(() => {
        if (body) {
          dispatch(
            updateCarIfNeeded(
              token,
              selectedCar?.id!,
              isClearComment ? { ...body, noteText: '' } : body
            )
          );
        }

        submitPhotos();

        setIsAddPhotoEnabled(false);
        dispatch(closeModal('updateStateModal'));
        setTimeout(
          () => dispatch(fetchVehicleEventsForCar(selectedCar?.id!)),
          300
        );
      });
    } else {
      dispatch(
        createStatusChangeBooking(
          isWithoutEndDate
            ? {
                ...body,
                type: 'statusChange',
                requestedStatus: body?.status,
                carId: selectedCar?.id,
                ...(startDate && { from: startDate?.toISOString() }),
                statusChangeNote: body?.noteText,
                statusChangeBlockReasons: body?.blockReasons,
              }
            : {
                ...body,
                type: 'statusChange',
                requestedStatus: body?.status,
                carId: selectedCar?.id,
                from: startDate?.toISOString(),
                to: endDate?.toISOString(),
                statusChangeNote: body?.noteText,
                statusChangeBlockReasons: body?.blockReasons,
              },
          () => {
            submitPhotos();
            setIsAddPhotoEnabled(false);
            dispatch(closeModal('updateStateModal'));
            dispatch(getActiveBooking(displayCars!.map((car) => car.id)));
          }
        )
      );
    }
  };

  const handleOnCancel = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    dispatch(closeModal('updateStateModal'));
    clearStatusImages();
  };

  const onChangeStatusType = (e: RadioChangeEvent) => {
    setStatusChangeType(e.target.value);
  };

  const onClickWithoutEndDate = (e: CheckboxChangeEvent) => {
    setIsWithoutEndDate(e.target.checked);
  };

  const onClickClearComment = (e: CheckboxChangeEvent) => {
    setClearComment(e.target.checked);
    setState({ ...state, comment: '' });
  };

  const onClickDisableAddPhoto = (e) => {
    setIsAddPhotoEnabled(e.target.checked);
    setStatusImages([]);
    setImageURI('');
  };

  const handleAddingPhoto = async (fileList) => {
    setStatusImages(fileList);

    if (fileList.length === 0) {
      setImageURI('');
      return;
    }

    const latestFile = fileList[fileList.length - 1];

    if (latestFile?.originFileObj) {
      const uri = await getBase64(latestFile.originFileObj);
      setImageURI(uri);
    }
  };

  return (
    <>
      <DrawerModal
        visible={visibleModal}
        onClose={() => {
          dispatch(closeModal('updateStateModal'));
        }}
      >
        <div
          data-testid="UpdateCarStatusModal-modalWindow"
          className="update-car-status-modal"
        >
          <div className="status-selector-wrapper">
            <p className="label">Status</p>
            <CarStatusSelector
              currentStatus={currentStatus}
              value={state.nextStatus}
              onSelect={onSelectCarStatus}
            />
          </div>
          {state.nextStatus === CAR_STATUSES.BLOCKED && (
            <div className="reason-selector-wrapper">
              <p className="label">Reason</p>
              <BlockedReasonSelector
                value={state.blockedReasons}
                onSelect={onSelectReason}
              />
            </div>
          )}
          {currentStatus !== CAR_STATUSES.RELEASED &&
            state.nextStatus === CAR_STATUSES.RELEASED &&
            selectedCar?.availableCircles?.length !== 0 &&
            selectedCar?.fleetId && (
              <div className="circle-selector-wrapper">
                <p className="label">Circle</p>
                <CircleSelector
                  fleetId={selectedCar?.fleetId}
                  value={state.circle}
                  onSelect={onSelectCircle}
                />
              </div>
            )}

          <Checkbox
            data-testid="UpdateCarStatusModal-clearNoteCheckbox"
            style={{ marginBottom: 8, marginTop: 8 }}
            value={isClearComment}
            onChange={onClickClearComment}
          >
            {intl.formatMessage({
              id: 'fleetadmin.car.status.clear.note',
            })}
          </Checkbox>

          <div className="status-selector-wrapper">
            <p className="label">Comment (Optional)</p>
            <textarea
              className="comment-area"
              placeholder="Type here ..."
              value={state.comment}
              onChange={(e) => setState({ ...state, comment: e.target.value })}
              draggable={false}
              disabled={isClearComment}
            >
              Type here ...
            </textarea>
          </div>

          <>
            <p className="period-label">
              {intl.formatMessage({ id: 'fleetadmin.car.status.range' })}
            </p>
            <Radio.Group onChange={onChangeStatusType} value={statusChaneType}>
              <div className="radio-wrapper">
                <div className="row" style={{ marginBottom: 16 }}>
                  <Radio
                    value={1}
                    disabled={selectedCar?.status === CAR_STATUSES.RENTED}
                  >
                    {intl.formatMessage({
                      id: 'fleetadmin.car.status.range.instant',
                    })}
                  </Radio>
                </div>
                <div className="row">
                  <Radio value={2} />
                  <div style={{ fontSize: 15, width: 35 }}>
                    {intl.formatMessage({
                      id: 'fleetadmin.car.status.range.from',
                    })}
                  </div>
                  <div style={{ marginLeft: 8, marginRight: 8, width: 180 }}>
                    <DatePicker
                      getPopupContainer={(triggerNode: HTMLElement) => {
                        return triggerNode.parentNode as HTMLElement;
                      }}
                      disabledDate={(date) =>
                        // @ts-ignore
                        isBefore(new Date(date), new Date())
                      }
                      style={{ minWidth: 'unset' }}
                      format="DD.MM.YYYY HH:mm"
                      // @ts-ignore
                      showTime={{
                        format: 'HH:mm',
                        minuteStep: 15,
                      }}
                      onChange={(value) => {
                        setStartDate(value);
                        setStatusChangeType(2);
                      }}
                      popupStyle={
                        isDesktop()
                          ? { position: 'absolute', left: '-30%' }
                          : { position: 'absolute', left: '0%' }
                      }
                    />
                  </div>
                </div>
                <div className="row" style={{ marginLeft: 25, marginTop: 8 }}>
                  <div style={{ fontSize: 15, width: 35 }}>
                    {intl.formatMessage({
                      id: 'fleetadmin.car.status.range.to',
                    })}
                  </div>
                  <div style={{ marginLeft: 8, marginRight: 8, width: 180 }}>
                    <DatePicker
                      getPopupContainer={(triggerNode: HTMLElement) => {
                        return triggerNode.parentNode as HTMLElement;
                      }}
                      disabled={isWithoutEndDate}
                      disabledDate={(date) =>
                        // @ts-ignore
                        isBefore(new Date(date), new Date())
                      }
                      style={{ minWidth: 'unset' }}
                      format="DD.MM.YYYY HH:mm"
                      // @ts-ignore
                      showTime={{
                        format: 'HH:mm',
                        minuteStep: 15,
                      }}
                      onChange={(value) => {
                        setEndDate(value);
                        setStatusChangeType(2);
                      }}
                      popupStyle={
                        isDesktop()
                          ? { position: 'absolute', left: '-30%' }
                          : { position: 'absolute', left: '0%' }
                      }
                    />
                  </div>
                  <Checkbox onChange={onClickWithoutEndDate}>
                    {intl.formatMessage({
                      id: 'fleetadmin.car.status.range.without_end_date',
                    })}
                  </Checkbox>
                </div>
              </div>
            </Radio.Group>
          </>

          <Checkbox
            data-testid="UpdateCarStatusModal-addImageCheckbox"
            style={{ marginBottom: 8, marginTop: 8 }}
            value={isAddPhotoEnabled}
            onChange={onClickDisableAddPhoto}
          >
            {intl.formatMessage({
              id: 'fleetadmin.status.add.photo',
            })}
          </Checkbox>

          <div
            style={{
              marginTop: 8,
            }}
            hidden={!isAddPhotoEnabled}
          >
            <p className="label">
              {intl.formatMessage({
                id: `fleetadmin.status.add.photo.label`,
              })}
            </p>
            <Upload
              accept="image/*"
              listType="picture-card"
              maxCount={1}
              fileList={statusImages}
              beforeUpload={() => false}
              onChange={({ fileList }) => handleAddingPhoto(fileList)}
              showUploadList={{
                showRemoveIcon: true,
                showPreviewIcon: false,
              }}
            >
              {statusImages.length < 1 && <Button icon={<PlusOutlined />} />}
            </Upload>
          </div>

          <Button
            data-testid="UpdateCarStatusModal-submitBtn"
            className="submit-button"
            onClick={handleOnSubmit}
            disabled={
              (state.nextStatus === CAR_STATUSES.BLOCKED &&
                state?.blockedReasons?.length === 0) ||
              (isNoteOnlyUpdate && !hasNote && !hasImage)
            }
          >
            {intl.formatMessage({
              id: `submit`,
            })}
          </Button>
          <Button
            data-testid="UpdateCarStatusModal-cancelBtn"
            className="cancel-button"
            onClick={handleOnCancel}
          >
            {intl.formatMessage({
              id: `cancel`,
            })}
          </Button>
        </div>
      </DrawerModal>
      <ConfirmBlockModal
        onUpdate={(e, proceed) => handleOnSubmit(e, proceed)}
      />
      <style jsx>{styles}</style>
    </>
  );
};

export default UpdateCarStatusModal;
